import { css, CSSInterpolation } from '@emotion/css';
import { useTheme } from '@emotion/react';
import { Chip } from '@material-ui/core';
import React from 'react';
import { FullTheme } from '../../themes';

export type Color =
  | 'red'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'gray'
  | 'darkGray'
  | 'black'
  | 'none';

export const getColor = (t: FullTheme, color: Color): [string, string] => {
  const { colors } = t.custom;
  switch (color) {
    case 'yellow':
      return [colors.yellow.contrastText, colors.yellow.w500];
    case 'gray':
      return [colors.gray.contrastText, colors.gray.w400];
    case 'darkGray':
      return [colors.gray.contrastText, colors.gray.w500];
    case 'blue':
      return [colors.blue.contrastText, colors.blue.w400];
    case 'red':
      return [colors.red.contrastText, colors.red.w400];
    case 'green':
      return [colors.green.contrastText, colors.green.w400];
    case 'purple':
      return [colors.purple.contrastText, colors.purple.w400];
    case 'black':
      return [colors.gray.white, colors.gray.w700];
    case 'none':
      return [colors.gray.white, colors.gray.white];
    default:
      return [colors.gray.white, color];
  }
};

export const getColorLight = (t: FullTheme, color: Color): [string, string] => {
  const { colors } = t.custom;
  switch (color) {
    case 'yellow':
      return [colors.yellow.w800, colors.yellow.w200];
    case 'gray':
      return [colors.gray.w600, colors.gray.w200];
    case 'darkGray':
      return [colors.gray.w800, colors.gray.w300];
    case 'blue':
      return [colors.blue.w600, colors.blue.w100];
    case 'red':
      return [colors.red.w600, colors.red.w100];
    case 'green':
      return [colors.green.w600, colors.green.w100];
    case 'purple':
      return [colors.purple.w600, colors.purple.w100];
    case 'black':
      return [colors.gray.white, colors.gray.w600];
    default:
      return [colors.gray.white, color];
  }
};

type Props = {
  color: Color;
  size?: 'medium' | 'small';
  variant?: 'rounded' | 'squared';
  width?: number | string;
  muiVariant?: 'default' | 'outlined';
};

// TODO - reuse this for the other variants
export const ColoredChipCustom: React.FC<
  Omit<Props, 'color'> & {
    color: string;
    backgroundColor: string;
    borderColor: string;
  }
> = ({
  color,
  backgroundColor,
  borderColor,
  size = 'medium',
  variant = 'rounded',
  width,
  children,
  muiVariant
}) => {
  const theme = useTheme();
  const rootClassName: CSSInterpolation = {
    color: `${color} !important`,
    backgroundColor: `${backgroundColor} !important`,
    width,
    cursor: 'inherit !important',
    fontWeight: theme.typography.fontWeightLight,
    border: `1px solid ${borderColor} !important`,
    borderColor: `${borderColor}`
  };
  if (variant === 'squared') {
    rootClassName.borderRadius = '6px !important';
  }
  return (
    <Chip
      variant={muiVariant}
      label={children}
      size={size}
      classes={{
        root: css(rootClassName)
      }}
    />
  );
};

export const ColoredChipDark: React.FC<Props> = ({
  color,
  size = 'medium',
  variant = 'rounded',
  width,
  children,
  muiVariant
}) => {
  const theme = useTheme();
  const [fgColor, bgColor] = getColor(theme, color);

  const _color = muiVariant
    ? color === 'darkGray'
      ? '#5c5b5b'
      : `${color}`
    : `${fgColor}`;
  const _backgroundColor = muiVariant ? 'white' : bgColor;
  const _borderColor = muiVariant ? bgColor : 'none';

  return (
    <ColoredChipCustom
      color={_color}
      backgroundColor={_backgroundColor}
      borderColor={_borderColor}
      width={width}
      muiVariant={muiVariant}
      size={size}
      variant={variant}
    >
      {children}
    </ColoredChipCustom>
  );
};

export const normalizeColor = (
  color: string,
  theme: FullTheme
): Color | string => {
  const cs = theme.custom.colors;
  switch (color) {
    case cs.blue.w100:
    case cs.blue.w200:
    case cs.blue.w300:
    case cs.blue.w400:
    case cs.blue.w500:
    case cs.blue.w700:
    case cs.blue.w900:
      return 'blue';
    case cs.green.w100:
    case cs.green.w200:
    case cs.green.w300:
    case cs.green.w400:
    case cs.green.w500:
    case cs.green.w700:
    case cs.green.w900:
      return 'green';
    case cs.purple.w100:
    case cs.purple.w200:
    case cs.purple.w300:
    case cs.purple.w400:
    case cs.purple.w500:
    case cs.purple.w700:
    case cs.purple.w900:
      return 'purple';
    case cs.red.w100:
    case cs.red.w200:
    case cs.red.w300:
    case cs.red.w400:
    case cs.red.w500:
    case cs.red.w700:
    case cs.red.w900:
      return 'red';
    case cs.yellow.w100:
    case cs.yellow.w200:
    case cs.yellow.w300:
    case cs.yellow.w400:
    case cs.yellow.w500:
    case cs.yellow.w700:
    case cs.yellow.w900:
      return 'yellow';
    default:
      return color;
  }
};

export const ColoredChipLight: React.FC<Props> = ({
  color,
  size = 'medium',
  variant = 'rounded',
  width,
  children
}) => {
  const theme = useTheme();
  const [fgColor, bgColor] = getColorLight(theme, color);
  return (
    <ColoredChipCustom
      width={width}
      size={size}
      color={fgColor}
      backgroundColor={bgColor}
      borderColor="none"
      variant={variant}
    >
      {children}
    </ColoredChipCustom>
  );
};

export const ColoredChip: React.FC<Props & { mode?: 'light' | 'normal' }> = ({
  mode = 'light',
  ...props
}) => {
  const Comp = mode === 'light' ? ColoredChipLight : ColoredChipDark;
  return <Comp {...props} />;
};
